"use client";

import clsx from "clsx";
import { useState } from "react";
import FormControl, {
  type FormControlInfo,
  type FormControlProps,
  useFormControl,
} from "src/components/form/FormControl/FormControl";

export interface PathParameterFieldProps
  extends Pick<FormControlProps, "className" | "defaultValue" | "disabled" | "name"> {
  placeholder: string;
}

const PathParameterInput = (props: Pick<PathParameterFieldProps, "placeholder">) => {
  const { placeholder } = props;
  const formControl = useFormControl();

  // Keep FormControl-only props off the DOM input; it is always controlled through `value` below.
  const {
    onValueProcess: _onValueProcess,
    error: _error,
    label: _label,
    defaultValue: rawDefaultValue,
    value: rawValue,
    ...inputProps
  } = formControl?.inputProps ?? ({} as Partial<FormControlInfo["inputProps"]>);
  const value = rawValue?.toString() || "";
  const defaultValue = rawDefaultValue?.toString() || "";
  const [stateValue, setStateValue] = useState(value ?? defaultValue ?? "");
  const resolvedValue = value ?? stateValue;
  const displayText = resolvedValue || `<${placeholder}>`;

  return (
    <span className="relative inline-block">
      <input
        type="text"
        placeholder={`<${placeholder}>`}
        className="inline-block bg-transparent outline-none font-mono text-inherit border-b border-dashed border-current"
        // As wide as its text in the monospace font (`ch`), so the server renders the final width
        // and nothing moves when the field hydrates.
        style={{ borderBottomWidth: "1px", width: `max(40px, ${displayText.length}ch)` }}
        {...inputProps}
        value={resolvedValue}
        onChange={(event) => {
          setStateValue(event.target.value);
          inputProps.onChange?.(event);
        }}
        ref={inputProps.ref}
      />
    </span>
  );
};

const PathParameterField = (props: PathParameterFieldProps) => {
  const { placeholder, className, ...formControlProps } = props;

  return (
    <FormControl {...formControlProps} className={clsx("inline-block", className)}>
      <FormControl.Input>
        <PathParameterInput placeholder={placeholder} />
      </FormControl.Input>
    </FormControl>
  );
};

export default PathParameterField;
